import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, profileRequest, groupConfig, graphConfig } from '../msalConfig';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const msalAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [loading, setLoading] = useState(true);

  const account = accounts[0] || null;

  /**
   * Acquire an access token silently, falling back to redirect if needed.
   */
  const getAccessToken = useCallback(async (scopes) => {
    if (!account) throw new Error('No account available');
    try {
      const response = await instance.acquireTokenSilent({ scopes, account });
      return response.accessToken;
    } catch {
      // Redirect instead of popup to avoid browser popup blockers
      await instance.acquireTokenRedirect({ scopes });
      // After redirect, the page reloads — this line won't execute
      return null;
    }
  }, [instance, account]);

  /**
   * After login, determine user profile + group membership.
   * Strategy: token claims first, then Graph API fallback.
   */
  const fetchUserInfo = useCallback(async () => {
    if (!account || inProgress !== InteractionStatus.None) return;

    try {
      // 1. Check for groups in token claims (fastest, no API call)
      const tokenGroups = account.idTokenClaims?.groups;
      let adminMatch = false;
      let memberMatch = false;

      if (Array.isArray(tokenGroups) && tokenGroups.length > 0) {
        adminMatch = tokenGroups.includes(groupConfig.goaAdminGroupId);
        memberMatch = tokenGroups.includes(groupConfig.goaMembersGroupId);
      } else {
        // 2. Fallback: call Graph API to get group membership
        try {
          const token = await getAccessToken(profileRequest.scopes);
          const groupsRes = await fetch(`${graphConfig.graphEndpoint}/me/memberOf`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (groupsRes.ok) {
            const groupsData = await groupsRes.json();
            const groups = groupsData.value || [];
            adminMatch = groups.some(
              (g) =>
                g.id === groupConfig.goaAdminGroupId ||
                g.displayName === groupConfig.goaAdminGroupName
            );
            memberMatch = groups.some(
              (g) =>
                g.id === groupConfig.goaMembersGroupId ||
                g.displayName === groupConfig.goaMembersGroupName
            );
          }
        } catch (apiErr) {
          console.warn('Could not fetch groups via API:', apiErr.message);
        }
      }

      // 3. Fetch basic profile from Graph (include otherMails & identities for CIAM)
      let profile = null;
      try {
        const token = await getAccessToken(profileRequest.scopes);
        const profileRes = await fetch(`${graphConfig.graphEndpoint}/me?$select=id,displayName,mail,otherMails,identities,userPrincipalName,givenName,surname`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (profileRes.ok) {
          profile = await profileRes.json();
        }
      } catch {
        // Use account info as fallback
      }

      // Extract actual sign-in email (CIAM stores it in identities or otherMails, not mail/UPN)
      const signInEmail = (() => {
        // Check identities array for emailAddress sign-in type
        if (profile?.identities?.length) {
          const emailIdentity = profile.identities.find(
            i => i.signInType === 'emailAddress' || i.signInType === 'federated'
          );
          if (emailIdentity?.issuerAssignedId) return emailIdentity.issuerAssignedId;
        }
        // Check otherMails
        if (profile?.otherMails?.length) return profile.otherMails[0];
        // Check mail field
        if (profile?.mail) return profile.mail;
        // Check token claims
        if (account.idTokenClaims?.email) return account.idTokenClaims.email;
        if (account.idTokenClaims?.preferred_username) return account.idTokenClaims.preferred_username;
        // UPN might be GUID-based in CIAM - only use if it looks like an email
        if (profile?.userPrincipalName && !profile.userPrincipalName.match(/^[0-9a-f]{8}-/)) {
          return profile.userPrincipalName;
        }
        return account.username;
      })();

      setUser({
        id: profile?.id || account.localAccountId,
        displayName: profile?.displayName || account.name || account.username,
        email: signInEmail,
        signInEmail: signInEmail,
        givenName: profile?.givenName,
        surname: profile?.surname,
      });

      setIsAdmin(adminMatch);
      setIsMember(adminMatch || memberMatch);
    } catch (err) {
      console.error('Error initializing user info:', err);
      // Minimal fallback so the user is still logged in
      setUser({
        id: account.localAccountId,
        displayName: account.name || account.username,
        email: account.username,
      });
      setIsMember(true);
    } finally {
      setLoading(false);
    }
  }, [account, inProgress, getAccessToken]);

  useEffect(() => {
    if (msalAuthenticated && account && inProgress === InteractionStatus.None) {
      fetchUserInfo();
    } else if (!msalAuthenticated && inProgress === InteractionStatus.None) {
      setUser(null);
      setIsAdmin(false);
      setIsMember(false);
      setLoading(false);
    }
  }, [msalAuthenticated, account, inProgress, fetchUserInfo]);

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('Login failed:', err);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({ postLogoutRedirectUri: '/' });
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [instance]);

  const value = {
    isAuthenticated: msalAuthenticated,
    user,
    userEmail: user?.email || account?.username || null,
    isAdmin,
    isMember,
    loading: loading || inProgress !== InteractionStatus.None,
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
